import { List } from 'antd';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FormAnswerImage, FormInstanceApi } from 'api/form';
import { ReactComponent as SearchSvg } from 'assets/svgs/search.svg';
import { Button } from 'components/atoms/button';
import { Modal } from 'components/atoms/modal';
import { sortByText } from 'utils/sort-by-text';
import {
  AttachmentListContainer,
  EmptyWrapper,
} from './attachment-modal.styles';
import { ListType, AttachmentRenderer } from './attachment-renderer';
import { AttachmentsFilter, SortOptions } from './attachments-filters';

const gridConfig = { gutter: 16, column: 2 };

export interface AttachmentFormAnswerImage extends FormAnswerImage {
  createdByUserName: string;
  fileExt: string;
}

const sortByRecent = (list: AttachmentFormAnswerImage[]) =>
  list.sort((a, b) => a.attachmentId - b.attachmentId);
const sortByOldest = (list: AttachmentFormAnswerImage[]) =>
  list.sort((a, b) => b.attachmentId - a.attachmentId);
const sortByAsc = (list: AttachmentFormAnswerImage[]) =>
  list.sort((a, b) => sortByText(a.name, b.name));
const sortByDesc = (list: AttachmentFormAnswerImage[]) =>
  list.sort((a, b) => sortByText(b.name, a.name));

export const AttachmentsModal = ({
  attachmentsList = [],
  visible = true,
  onClose,
}: {
  attachmentsList: AttachmentFormAnswerImage[];
  visible?: boolean;
  onClose?: (value: boolean) => void;
}) => {
  const [listType, setListType] = useState<ListType>('list');
  const [searchText, setSearchText] = useState('');
  const [filteredList, setFilteredList] = useState<AttachmentFormAnswerImage[]>(
    sortByRecent(attachmentsList),
  );

  const onSearch = (searchValue = '') => {
    if (searchValue) {
      return setFilteredList((list) =>
        list.filter((attachment) => {
          if (
            attachment.name.toLowerCase().includes(searchValue.toLowerCase())
          ) {
            return true;
          }

          if (
            attachment.createdByUserName
              .toLowerCase()
              .includes(searchValue.toLowerCase())
          ) {
            return true;
          }

          if (
            attachment.fileExt.toLowerCase().includes(searchValue.toLowerCase())
          ) {
            return true;
          }

          return false;
        }),
      );
    }

    return setFilteredList([...attachmentsList]);
  };

  const handleSort = async (sortBy: SortOptions) => {
    switch (sortBy) {
      case SortOptions.ByNewestFirst:
      default: {
        setFilteredList((list) => [...sortByRecent(list)]);
        break;
      }

      case SortOptions.ByOldestFirst: {
        setFilteredList((list) => [...sortByOldest(list)]);
        break;
      }
      case SortOptions.ByAscending: {
        setFilteredList((list) => [...sortByAsc(list)]);
        break;
      }
      case SortOptions.ByDescending: {
        setFilteredList((list) => [...sortByDesc(list)]);
        break;
      }
    }
  };

  const attachmentsById = useMemo(
    () => attachmentsList.map((attachment) => attachment.attachmentId),
    [attachmentsList],
  );

  const handleAttachmentDownload = async (attachmentIds: number[]) => {
    if (attachmentIds.length) {
      const data = await FormInstanceApi.exportFormAttachments(attachmentIds);
      const link = document.createElement('a');

      link.href = URL.createObjectURL(new Blob([data]));
      link.setAttribute('download', 'attachments.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };
  const clearSearch = () => {
    setSearchText('');
    onSearch('');
  };

  return (
    <Modal
      visible={visible}
      title={<FormattedMessage id="attachments" />}
      onCancel={() => onClose?.(false)}
      footer={null}
    >
      <AttachmentsFilter
        listType={listType}
        searchText={searchText}
        setSearchText={setSearchText}
        count={filteredList.length}
        onListTypeChange={(value) => setListType(value)}
        onSearch={onSearch}
        onSortChange={handleSort}
        onDownload={() => handleAttachmentDownload(attachmentsById)}
      />
      <AttachmentListContainer>
        {filteredList.length ? (
          <List
            dataSource={filteredList}
            grid={listType === 'grid' ? gridConfig : undefined}
            renderItem={(attachment: AttachmentFormAnswerImage, key) => (
              <AttachmentRenderer
                key={key}
                attachment={attachment}
                type={listType}
              />
            )}
          />
        ) : (
          <EmptyWrapper>
            <SearchSvg />
            <div>
              <FormattedMessage id="timesheets.tasks.filters.error.no_search_result_line_1" />
            </div>
            <div>
              <FormattedMessage id="timesheets.tasks.filters.error.no_search_result_line_2" />
            </div>
            <Button type="link" onClick={clearSearch}>
              <FormattedMessage id="timesheets.tasks.filters.actions.clear_search" />
            </Button>
          </EmptyWrapper>
        )}
      </AttachmentListContainer>
    </Modal>
  );
};
